import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from '@emotion/styled';

import { getSpecialists } from "../apiService";

import { BasicFlex } from "../../../utils/Common";

import { useFormikContext } from "formik";

import { useFormData } from "../../../contexts/FormData";

import { Switch, FormControlLabel, TextField, InputAdornment } from "@mui/material";

import { useParams } from "react-router-dom";

import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';

import ErrorMessage from "../../../utils/MuiFields/ErrorMessage";

const List = styled(BasicFlex)`
`;

const ListHeader = styled(BasicFlex)`
  background-color: ${props => props.theme.colors.darkBg};
  padding: 1.5rem;
  padding-right: 0;

  div {
    flex-grow: 1;
    flex-basis: 0;
  }

  @media ${props => props.theme.breaks.down('md')} {
    div {
      padding: 0 .1rem;
    }
  }
`;

const ListItem = styled(BasicFlex)`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding-bottom: .6rem;
  padding-left: 1.5rem;

  div {
    flex-grow: 1;
    flex-basis: 0;
  }

  @media ${props => props.theme.breaks.down('md')} {
    div {
      padding: 0 .1rem;
    }
  }
`;

const SelectContainer = styled.div`

  @media ${props => props.theme.breaks.down('md')} {
    max-width: 6rem;
  }
`

const NumberField = styled(TextField)`
  max-width: 15rem;
`

const Stylists = (props) => {
  const { formValues, setFormValues } = useFormData() || {};
  const { values: {
    default_cost,
    default_price,
    default_duration_in_minutes,
    default_estimated_time_to_complete,
    in_house
  }, errors } = useFormikContext() || {};

  const { id } = useParams();

  const { data: stylists } = useQuery({
    queryKey: ["specialists", id, in_house],
    queryFn: () => getSpecialists(id, in_house),
    // staleTime: Infinity
  });

  useEffect(() => {
   if (stylists?.length) {
    setFormValues(ov => {
      return {
        ...ov,
        active_stylists: stylists?.map(stylist => {
          let newStylist = { ...stylist };
          if (!stylist.cost_mod) newStylist.cost = default_cost;
          if (!stylist.price_mod) newStylist.price = default_price;
          if (!stylist.estimated_time_to_complete_mod) newStylist.estimated_time_to_complete = default_estimated_time_to_complete;
          if (!stylist.duration_in_minutes_mod) newStylist.duration_in_minutes = default_duration_in_minutes;
          return newStylist;
        })
      }
    })
  }
  }, [stylists, default_price, default_duration_in_minutes, default_cost, default_estimated_time_to_complete]);


  const editStylist = (e, field, index, check = false) => {
    setFormValues(ov => {
      let mod = ["price", "duration_in_minutes", "cost", "estimated_time_to_complete"].includes(field) ? true : false;
      const newStylists = [...ov.active_stylists];
      newStylists[index][field] = check ? e.target.checked : e.target.value;
      if (mod && field === "cost") newStylists[index].cost_mod = true;
      
      if (mod && field === "price") newStylists[index].price_mod = true;
      if (mod && field === "duration_in_minutes") newStylists[index].duration_in_minutes_mod = true;
      if (mod && field === "estimated_time_to_complete") newStylists[index].estimated_time_to_complete_mod = true;
      return { ...ov, active_stylists: newStylists };
    })
  };

  return <List dir="column">
    <ListHeader >
      <SelectContainer></SelectContainer>
      <div>Specialist</div>
      <div>Type</div>
      <div>Cost</div>
      <div>Price</div>
      <div>Duration</div>
      <div>ETA</div>
    </ListHeader>
    {formValues?.active_stylists?.map((stylist, index) => (
      <ListItem key={stylist.stylist_id} my="0.5rem" >
       <SelectContainer><FormControlLabel control={<Switch size="small" checked={stylist.is_active} onChange={(e) => editStylist(e, "is_active", index, true)} />} /></SelectContainer>
       <div>{stylist.full_name}</div>
       <div>{in_house ? "In House" : "External"}</div>
       <div>
        <NumberField 
          type="number" 
          size="small" 
          value={stylist.cost}
          onChange={(e) => editStylist(e, "cost", index)} 
          disabled={!stylist.is_active} 
          slotProps={{input: {
            startAdornment: <InputAdornment position="start"><AttachMoneyRoundedIcon fontSize="small" color="icon" /></InputAdornment>
          }}} 
        />
       </div>
       <div>
        <NumberField 
          type="number" 
          size="small" 
          value={stylist.price} 
          onChange={(e) => editStylist(e, "price", index)} 
          disabled={!stylist.is_active} 
          slotProps={{input: {
            startAdornment: <InputAdornment position="start"><AttachMoneyRoundedIcon fontSize="small" color="icon" /></InputAdornment>
          }}} 
        />
       </div>
       <div>
        <NumberField 
          type="number" 
          size="small" 
          value={stylist.duration_in_minutes} 
          onChange={(e) => editStylist(e, "duration_in_minutes", index)}
          disabled={!stylist.is_active} 
          slotProps={{input: {
          startAdornment: <InputAdornment position="start"><AccessTimeRoundedIcon fontSize="small" color="icon" /></InputAdornment>
          }}} 
        />
       </div>
       <div>
        <NumberField 
          type="number" 
          size="small" 
          value={stylist.estimated_time_to_complete} 
          onChange={(e) => editStylist(e, "estimated_time_to_complete", index)}
          disabled={!stylist.is_active} 
          slotProps={{input: {
          startAdornment: <InputAdornment position="start"><AccessTimeRoundedIcon fontSize="small" color="icon" /></InputAdornment>
          }}} 
        />
       </div>
      </ListItem>
      ))}
      {Object.keys(errors).find(key => key.includes("active_stylists")) && <ErrorMessage error message="Active Stylists require price and duration" />}
  </List>
};

export default Stylists;